/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Screen names for accounts that arrive without one.
//
// An anonymous sign-in creates a Firebase user with no displayName at all, so
// everywhere a person is named — a class roster, a submission row, the avatar
// menu — they read as "Anonymous". That is no use to a teacher looking for one
// student among thirty. A screen name is the student's own readable label; the
// UID stays the identity (the teacher sees both side by side on the roster).
//
// Three places have to agree:
//   1. the Firebase Auth profile (what onAuthStateChanged replays on reload),
//   2. users/{uid}.displayName (what the account page and server-side reads see),
//   3. classes/{classId}/members/{uid}.name — a roster row is a COPY, written at
//      join time, so a name set afterwards never reaches the teacher on its own.
//      That is the ordinary order of events here: a student joins as "Anonymous"
//      and only then thinks to say who they are, so the rename follows them into
//      every class they are in.

import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, firestore } from './firebase.ts';
import { useStore } from './stores/common.ts';

// The placeholder the app puts on an account with no name of its own
// (useAuthHandlers on anonymous sign-in, cloudManager on every auth replay).
// It is a label, never an identity — treat it as "no screen name yet".
export const NO_NAME = 'Anonymous';

export const MAX_SCREEN_NAME_LENGTH = 40;

// The user's own readable name, or null if all they have is the placeholder.
export const screenNameOf = (user: { displayName?: string | null } | null | undefined): string | null => {
  const name = user?.displayName?.trim();
  return name && name !== NO_NAME ? name : null;
};

export const sanitizeScreenName = (raw: string): string =>
  raw.trim().replace(/\s+/g, ' ').slice(0, MAX_SCREEN_NAME_LENGTH);

// Set the signed-in user's screen name everywhere it is stored. Returns false
// (and leaves nothing half-written that matters) if there is no signed-in user
// or the name is empty; throws only if the Auth profile write itself fails —
// the callers surface that. The roster propagation is best-effort per class:
// one unreachable class must not lose the rename in the other two.
export const setScreenName = async (raw: string): Promise<boolean> => {
  const name = sanitizeScreenName(raw);
  const current = auth.currentUser;
  if (!name || !current) return false;

  // 1. Auth profile — only the client holds the signed-in user.
  await updateProfile(current, { displayName: name });

  // 2. The user document.
  try {
    await setDoc(doc(firestore, 'users', current.uid), { displayName: name }, { merge: true });
  } catch (e) {
    console.warn('Failed to store screen name on the user document', e);
  }

  // 3. Every roster row that is a copy of the old name. `joinedClasses` is the
  //    same index fetchJoinedClasses reads. updateDoc (not setDoc-merge) on
  //    purpose: a merge would CREATE a member row without its `uid` field for a
  //    class the user has since left, which the security rules reject anyway.
  try {
    const snap = await getDoc(doc(firestore, 'users', current.uid));
    const joined: string[] = Array.isArray(snap.data()?.joinedClasses) ? snap.data()!.joinedClasses : [];
    await Promise.all(
      joined.map(async (classId) => {
        try {
          await updateDoc(doc(firestore, 'classes', classId, 'members', current.uid), { name });
        } catch {
          // Stale index entry (left/deleted class) — fetchJoinedClasses prunes it.
        }
      }),
    );
  } catch (e) {
    console.warn('Failed to propagate screen name to class rosters', e);
  }

  // 4. The in-memory user the whole UI renders from.
  useStore.getState().set((state) => {
    if (state.user) state.user.displayName = name;
  });
  return true;
};
